import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import {
  KeyIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"

/**
 * Modal for viewing and copying user credentials
 */
const UserCredentialsModal = ({ user, isOpen, onClose }) => {
  const [copiedStates, setCopiedStates] = useState({})

  if (!user) return null

  const credentials = [
    { key: "Email", value: user.email },
    { key: "Google ID", value: user.google_id },
    { key: "Apple ID", value: user.apple_id },
    { key: "Facebook ID", value: user.facebook_id },
    { key: "Internal ID", value: user.internal_id },
    { key: "Stripe Customer ID", value: user.stripe_customer_id },
  ].filter(cred => cred.value)

  const handleCopy = async (value, index, credentialName) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedStates(prev => ({ ...prev, [index]: true }))

      // Show success notification
      showNotification({
        message: `${credentialName} copied to clipboard`,
        className: "bg-admin_primary text-admin_text_inv"
      })

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [index]: false }))
      }, 2000)
    } catch (error) {
      console.error("Failed to copy text:", error)
      showNotification({
        message: "Failed to copy to clipboard",
        className: "bg-admin_error text-white"
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title={`User Credentials`}
      className="max-w-3xl"
    >
      <div className="space-y-6">
        {/* User info header */}
        <div className="flex items-center gap-3 bg-admin_primary/10 rounded-lg border border-admin_bordered p-4">
          <div className="w-12 h-12 flex items-center justify-center bg-admin_primary rounded-full flex-shrink-0">
            <Typography
              variant="admin_h4"
              className="text-white"
            >
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </Typography>
          </div>

          <div>
            <Typography variant="admin_h4" className="font-medium">
              {user.name || "Unknown User"}
            </Typography>

            <Typography
              variant="admin_txt1"
              className="text-admin_text_secondary"
            >
              {user.email}
            </Typography>
          </div>
        </div>

        {/* Credentials grid */}
        {credentials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {credentials.map((cred, index) => (
              <div key={index} className="border border-admin_bordered rounded-lg hover:border-admin_primary/30 p-4  transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-admin_primary/10 rounded-full">
                    <KeyIcon className="w-4 h-4 text-admin_primary" />
                  </div>

                  <Typography variant="admin_label" className="my-0">
                    {cred.key}
                  </Typography>
                </div>

                <div className="bg-admin_canvas rounded border border-admin_bordered/50 p-3 group">
                  <div className="flex items-center justify-between">
                    <Typography
                      variant="admin_txt1_dim"
                      className="font-mono break-all flex-1 pr-3"
                    >
                      {cred.value}
                    </Typography>

                    <Button
                      variant="admin_icon"
                      onClick={() => handleCopy(cred.value, index, cred.key)}
                    >
                      {copiedStates[index] ? (
                        <CheckIcon className="w-4 h-4 text-admin_success" />
                      ) : (
                        <Tooltip content="Copy to clipboard">
                          <ClipboardDocumentIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_primary" />
                        </Tooltip>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-admin_canvas flex items-center justify-center mx-auto mb-4">
              <KeyIcon className="w-8 h-8 text-admin_text_secondary" />
            </div>

            <Typography
              variant="admin_h4"
              className="text-admin_text_secondary mb-2"
            >
              No Credentials Found
            </Typography>

            <Typography variant="admin_txt1_dim">
              This user has no authentication credentials configured
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default UserCredentialsModal
